import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/agb")({
  head: () => ({
    meta: [
      { title: "AGB – Energie Prime 360" },
      { name: "description", content: "Allgemeine Geschäftsbedingungen der Plattform Energie Prime 360." },
    ],
  }),
  component: AGBPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <h2 className="font-display text-2xl pt-6">{title}</h2>
      {children}
    </>
  );
}

function AGBPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />
      <main className="mx-auto max-w-3xl px-6 md:px-10 py-20 md:py-28">
        <Link to="/" className="eyebrow text-[var(--leaf)] hover:underline">← Zurück</Link>
        <h1 className="font-display text-4xl md:text-5xl mt-6 mb-10">Allgemeine Geschäftsbedingungen (AGB)</h1>
        <div className="space-y-4 text-base leading-relaxed text-foreground/85">
          <Section title="1. Geltungsbereich">
            <p>
              Diese Allgemeinen Geschäftsbedingungen gelten für sämtliche Leistungen, Informationen,
              Analysen, Berechnungen, Vergleiche, Beratungsleistungen und digitalen Angebote der
              Plattform „Energie Prime 360", einem Produkt der Energie- &amp; Fördermittel Zentrum
              Deutschland GmbH.
            </p>
            <p>Mit der Nutzung der Plattform oder der Inanspruchnahme von Leistungen erkennt der Nutzer diese AGB an.</p>
          </Section>
          <Section title="2. Leistungsgegenstand">
            <p>
              Energie Prime 360 unterstützt Nutzer bei der Analyse, Bewertung und dem Vergleich von
              Energie-, Fördermittel-, Finanzierungs- und Investitionsvorhaben.
            </p>
            <p>Die Leistungen können insbesondere umfassen:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Analyse bestehender Situationen</li>
              <li>Angebots- und Wirtschaftlichkeitsvergleiche</li>
              <li>Fördermittelanalysen</li>
              <li>technische und wirtschaftliche Potenzialanalysen</li>
              <li>Investitions- und Wirtschaftlichkeitsberechnungen</li>
              <li>Handlungsempfehlungen und Entscheidungsgrundlagen</li>
              <li>digitale Informations- und Vergleichsdienste</li>
            </ul>
            <p>
              Ein Anspruch auf eine bestimmte Förderung, Einsparung, Rendite, Wirtschaftlichkeit
              oder wirtschaftlichen Erfolg besteht nicht.
            </p>
          </Section>
          <Section title="3. Unverbindlichkeit von Berechnungen">
            <p>
              Sämtliche Berechnungen, Prognosen, Wirtschaftlichkeitsanalysen, Fördermittelbewertungen,
              Einsparungsprognosen, Amortisationsberechnungen und sonstigen Auswertungen beruhen
              auf den zum jeweiligen Zeitpunkt verfügbaren Informationen und Annahmen.
            </p>
            <p>Sie stellen keine Garantie, Zusicherung oder verbindliche Vorhersage zukünftiger Ergebnisse dar.</p>
            <p>
              Tatsächliche Ergebnisse können insbesondere aufgrund geänderter Marktbedingungen,
              Energiepreise, Förderbedingungen, gesetzlicher Regelungen, technischer Entwicklungen
              oder individueller Nutzungsgewohnheiten abweichen.
            </p>
          </Section>
          <Section title="4. Fördermittel">
            <p>Förderprogramme, Zuschüsse und Finanzierungsmöglichkeiten unterliegen laufenden Änderungen.</p>
            <p>Eine Gewährung von Fördermitteln kann ausschließlich durch die jeweils zuständigen Förderstellen erfolgen.</p>
            <p>Energie Prime 360 übernimmt keine Gewähr für die Bewilligung, Höhe oder Verfügbarkeit von Fördermitteln.</p>
          </Section>
          <Section title="5. Steuerliche Hinweise">
            <p>
              Die im Rahmen der Leistungen bereitgestellten Informationen zu steuerlichen
              Potenzialen, Abschreibungen, Investitionsabzugsbeträgen, Sonderabschreibungen oder
              sonstigen steuerlichen Gestaltungsmöglichkeiten dienen ausschließlich der allgemeinen
              Information.
            </p>
            <p>Sie stellen keine Steuerberatung im Sinne des Steuerberatungsgesetzes dar.</p>
            <p>
              Verbindliche steuerliche Bewertungen und Entscheidungen sind ausschließlich mit dem
              jeweiligen Steuerberater oder einer hierzu befugten Person abzustimmen.
            </p>
          </Section>
          <Section title="6. Technische und wirtschaftliche Vergleiche">
            <p>
              Vergleiche erfolgen auf Grundlage der vom Nutzer bereitgestellten Informationen sowie
              öffentlich zugänglicher oder von Anbietern bereitgestellter Daten.
            </p>
            <p>
              Trotz sorgfältiger Prüfung kann keine Gewähr für Vollständigkeit, Aktualität oder
              Fehlerfreiheit sämtlicher Angaben übernommen werden.
            </p>
          </Section>
          <Section title="7. Mitwirkungspflichten des Nutzers">
            <p>
              Der Nutzer ist verpflichtet, alle für die Analyse erforderlichen Informationen
              vollständig und wahrheitsgemäß bereitzustellen.
            </p>
            <p>Unvollständige oder fehlerhafte Angaben können die Qualität der Ergebnisse beeinflussen.</p>
          </Section>
          <Section title="8. Haftungsbeschränkung">
            <p>
              Die Haftung für einfache Fahrlässigkeit ist ausgeschlossen, soweit keine wesentlichen
              Vertragspflichten verletzt werden.
            </p>
            <p>
              Hiervon unberührt bleiben Ansprüche aus Vorsatz, grober Fahrlässigkeit, Verletzung
              von Leben, Körper oder Gesundheit sowie gesetzlich zwingende Haftungsvorschriften.
            </p>
          </Section>
          <Section title="9. Urheberrechte">
            <p>
              Sämtliche Inhalte, Berechnungen, Analysen, Konzepte, Grafiken, Texte, Auswertungen
              und Dokumentationen unterliegen dem Urheberrecht.
            </p>
            <p>Eine Vervielfältigung, Weitergabe oder Veröffentlichung bedarf der vorherigen schriftlichen Zustimmung.</p>
          </Section>
          <Section title="10. Datenschutz">
            <p>Die Verarbeitung personenbezogener Daten erfolgt nach Maßgabe der jeweils geltenden Datenschutzerklärung.</p>
          </Section>
          <Section title="11. Änderungen der AGB">
            <p>Der Anbieter behält sich vor, diese AGB bei rechtlichen, technischen oder organisatorischen Änderungen anzupassen.</p>
          </Section>
          <Section title="12. Schlussbestimmungen">
            <p>Es gilt das Recht der Bundesrepublik Deutschland.</p>
            <p>
              Sollten einzelne Bestimmungen dieser AGB ganz oder teilweise unwirksam sein oder
              werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
            </p>
          </Section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
